import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { PRESENCE_ANIMATION } from '@/const'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { CellContext } from '@tanstack/react-table'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useMemo, useState } from 'react'
import { RowAction } from '../../types-v4'
import { ChannelThreadDetails } from './ChannelThreadDetails'
import { ThreadContent } from './ThreadContent'

export type MessagesCollaborationThreadDetails = RestSchemas['Messages.Collaboration.ThreadDetails']

export function Collaboration<T, V>({ action, info }: { action: RowAction; info: CellContext<T, V> }) {
  const { appId, workspaceId } = useAppContext()
  const { channelKey: channelKeyParam, threadKey: threadKeyParam } =
    (action.parameter_mappings as Record<'channelKey' | 'threadKey', string>) || {}

  if (!channelKeyParam || !threadKeyParam) return null

  const channelKey = (info.row.original as Record<string, string>)[channelKeyParam]
  const threadKey = (info.row.original as Record<string, string>)[threadKeyParam]
  if (!appId || !workspaceId) return null

  return (
    <CollaborationContent
      channelKey={channelKey}
      threadKey={threadKey}
      applicationId={appId}
      workspaceId={workspaceId}
    />
  )
}

function CollaborationContent({
  applicationId,
  workspaceId,
  channelKey,
  threadKey,
}: {
  applicationId: number
  workspaceId: number
  channelKey: string
  threadKey: string
}) {
  const [selectedThreadId, setSelectedThreadId] = useState<number | undefined>(undefined)

  const { getCollaborationApiGetChannelThreadDetailsByKey } = useRestClient()
  const invalidateThread = useCallback(() => {
    getCollaborationApiGetChannelThreadDetailsByKey.invalidateQueries({
      parameters: {
        path: { applicationId, workspaceId, channelKey, threadKey },
      },
    })
  }, [getCollaborationApiGetChannelThreadDetailsByKey, applicationId, workspaceId, channelKey, threadKey])
  const { data } = getCollaborationApiGetChannelThreadDetailsByKey.useSuspenseQuery({
    path: { applicationId, workspaceId, channelKey, threadKey },
  })

  const { channel } = data
  const { threads } = channel ?? {}

  const selectedThread = useMemo(
    () => threads?.find((thread) => thread.id === selectedThreadId),
    [threads, selectedThreadId]
  )

  return (
    <AnimatePresence mode="popLayout">
      {selectedThread ? (
        <motion.div key="thread-content" className="flex flex-col items-stretch gap-5 py-5" {...PRESENCE_ANIMATION}>
          <ThreadContent
            selectedThread={selectedThread}
            setSelectedThreadId={setSelectedThreadId}
            invalidateThread={invalidateThread}
          />
        </motion.div>
      ) : (
        <motion.div
          key="channel-thread-details"
          className="flex flex-col items-stretch gap-5 py-5"
          {...PRESENCE_ANIMATION}
        >
          <ChannelThreadDetails onSelectThread={setSelectedThreadId} channel={channel} threadKey={threadKey} />
        </motion.div>
      )}
    </AnimatePresence>
  )
}
